import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuthStore } from '../stores/authStore';

import FreeTrialBanner from '../components/FreeTrialBanner';
import SelectionCriteria from '../components/SelectionCriteria';
import PhotoGrid from '../components/PhotoGrid';
import ProcessingOptions from '../components/ProcessingOptions';
import ProcessingCTA from '../components/ProcessingCTA';
import usePhotoProvider from '../hooks/usePhotoProvider';
import usePhotoFilters from '../hooks/usePhotoFilters';
import useFreeTrialManagement from '../hooks/useFreeTrialManagement';

const buildExampleMetadata = (style, sentenceCount = 3, includeLenscribeTag = true) => {
  const baseExamples = {
    descriptive: {
      title: 'Golden Retriever in Park',
      sentences: [
        'A golden retriever sits on green grass in a sunny park.',
        'The dog appears happy with its tongue out, wagging its tail energetically.',
        'Trees and blue sky create a beautiful backdrop for this outdoor scene.',
        'Children can be seen playing in the background near playground equipment.',
        'The warm afternoon light creates perfect conditions for photography.'
      ],
      tags: ['dog', 'park', 'golden-retriever', 'grass', 'sunny', 'outdoor']
    },
    artistic: {
      title: 'Sunlit Companion',
      sentences: [
        "A gentle spirit of gold basking in nature's embrace.",
        "Shadows dance with light across the verdant canvas of earth.",
        "The soul of man's best friend radiates pure joy and serenity.",
        "Time stands still in this moment of perfect harmony between creature and creation.",
        "Golden fur catches sunbeams like captured starlight, warming the heart of all who witness this scene."
      ],
      tags: ['companion', 'golden', 'serenity', 'light', 'nature', 'joy']
    },
    fanciful: {
      title: "Captain Barksworth's Adventure",
      sentences: [
        'Our four-legged explorer surveys his grassy kingdom with the wisdom of a seasoned adventurer.',
        "With his royal golden coat gleaming in the sunshine, he's clearly plotting his next great expedition!",
        'The brave captain sniffs the air for signs of squirrels or hidden treasure.',
        'His wagging tail serves as both a flag of friendship and a signal to his loyal human crew.',
        "Legend says that whoever earns Captain Barksworth's trust will be blessed with endless belly rubs and slobbery kisses!"
      ],
      tags: ['adventure', 'explorer', 'captain', 'kingdom', 'golden', 'expedition']
    }
  };

  const example = baseExamples[style] || baseExamples.descriptive;
  const description = example.sentences.slice(0, sentenceCount).join(' ');

  const tags = [...example.tags];
  const appTag = process.env.REACT_APP_TAG || 'lenscribe';
  if (includeLenscribeTag && !tags.includes(appTag)) {
    tags.push(appTag);
  }

  return {
    title: example.title,
    description,
    tags
  };
};

const PhotoSelectionContent = ({
  photos,
  loading,
  criteriaCollapsed,
  setCriteriaCollapsed,
  albums,
  fetchingAlbums,
  selectedAlbum,
  setSelectedAlbum,
  getFeaturedAlbums,
  handleAlbumSelect,
  handleQuickAction,
  tags,
  setTags,
  handleTagSearch,
  uploadStart,
  uploadEnd,
  takenStart,
  takenEnd,
  handleDateChange,
  fetchPhotos,
  selectedPhotos,
  togglePhoto,
  toggleSelectAll,
  selectUntitledPhotos,
  scrollToProcessingOptions,
  freeTrialStatus,
  processingOptionsRef,
  selectedStyle,
  setSelectedStyle,
  descriptionSentences,
  setDescriptionSentences,
  settingUpBatch,
  loadingPreferences,
  loadingFreeTrial,
  handleStartProcessing,
  getExampleMetadata
}) => {
  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Select Photos for Metadata Generation</h1>

      <FreeTrialBanner
        freeTrialStatus={freeTrialStatus}
        photos={photos}
        selectedPhotos={selectedPhotos}
        scrollToProcessingOptions={scrollToProcessingOptions}
      />

      <SelectionCriteria
        photos={photos}
        loading={loading}
        criteriaCollapsed={criteriaCollapsed}
        setCriteriaCollapsed={setCriteriaCollapsed}
        albums={albums}
        fetchingAlbums={fetchingAlbums}
        selectedAlbum={selectedAlbum}
        setSelectedAlbum={setSelectedAlbum}
        getFeaturedAlbums={getFeaturedAlbums}
        handleAlbumSelect={handleAlbumSelect}
        handleQuickAction={handleQuickAction}
        tags={tags}
        setTags={setTags}
        handleTagSearch={handleTagSearch}
        uploadStart={uploadStart}
        uploadEnd={uploadEnd}
        takenStart={takenStart}
        takenEnd={takenEnd}
        handleDateChange={handleDateChange}
        fetchPhotos={fetchPhotos}
      />

      <PhotoGrid
        photos={photos}
        selectedPhotos={selectedPhotos}
        freeTrialStatus={freeTrialStatus}
        togglePhoto={togglePhoto}
        toggleSelectAll={toggleSelectAll}
        selectUntitledPhotos={selectUntitledPhotos}
        scrollToProcessingOptions={scrollToProcessingOptions}
      />

      {photos.length > 0 && (
        <>
          <div ref={processingOptionsRef}>
            <ProcessingOptions
              selectedStyle={selectedStyle}
              setSelectedStyle={setSelectedStyle}
              descriptionSentences={descriptionSentences}
              setDescriptionSentences={setDescriptionSentences}
              getExampleMetadata={getExampleMetadata}
            />
          </div>

          <ProcessingCTA
            selectedPhotos={selectedPhotos}
            freeTrialStatus={freeTrialStatus}
            selectedStyle={selectedStyle}
            descriptionSentences={descriptionSentences}
            settingUpBatch={settingUpBatch}
            loading={loading}
            loadingPreferences={loadingPreferences}
            loadingFreeTrial={loadingFreeTrial}
            handleStartProcessing={handleStartProcessing}
          />
        </>
      )}
    </div>
  );
};

const PhotoSelectionContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const { provider, checkingProvider } = usePhotoProvider({
    locationState: location.state,
    user,
    navigate
  });

  const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('descriptive');
  const [descriptionSentences, setDescriptionSentences] = useState(3);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [settingUpBatch, setSettingUpBatch] = useState(false);

  const processingOptionsRef = useRef(null);

  const {
    freeTrialStatus,
    loadingFreeTrial,
    ensureFreeTrialStatus
  } = useFreeTrialManagement();

  useEffect(() => {
    const loadPreferences = async () => {
      setLoadingPreferences(true);
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );

        const response = await Promise.race([
          axios.get('/api/users/preferences', { withCredentials: true }),
          timeoutPromise
        ]);

        const prefs = response.data.preferences;
        setSelectedStyle(prefs?.defaultStyle || 'descriptive');
        setDescriptionSentences(
          prefs?.descriptionSentences !== undefined ? prefs.descriptionSentences : 3
        );
      } catch (error) {
        console.error('Failed to load user data:', error);
        setSelectedStyle('descriptive');
        setDescriptionSentences(3);
      } finally {
        setLoadingPreferences(false);
      }
    };

    loadPreferences();
  }, []);

  const scrollToProcessingOptions = useCallback(() => {
    if (processingOptionsRef.current) {
      processingOptionsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  const handleFetchSuccess = useCallback(({ collapseCriteria }) => {
    if (collapseCriteria) {
      setCriteriaCollapsed(true);
    }
  }, []);

  const {
    albums,
    fetchingAlbums,
    selectedAlbum,
    setSelectedAlbum,
    getFeaturedAlbums,
    handleAlbumSelect,
    handleQuickAction: executeQuickAction,
    tags,
    setTags,
    handleTagSearch,
    uploadStart,
    uploadEnd,
    takenStart,
    takenEnd,
    handleDateChange,
    photos,
    loading,
    fetchPhotos,
    selectedPhotos,
    togglePhoto,
    toggleSelectAll: baseToggleSelectAll,
    selectUntitledPhotos: baseSelectUntitledPhotos
  } = usePhotoFilters({
    provider,
    loadingPreferences,
    loadingFreeTrial,
    onFetchSuccess: handleFetchSuccess
  });

  const handleQuickAction = useCallback(
    async (actionType) => {
      setCriteriaCollapsed(false);
      await executeQuickAction(actionType);
    },
    [executeQuickAction]
  );

  const toggleSelectAll = useCallback(() => {
    const shouldScroll = baseToggleSelectAll();
    if (shouldScroll) {
      setTimeout(() => {
        scrollToProcessingOptions();
      }, 100);
    }
  }, [baseToggleSelectAll, scrollToProcessingOptions]);

  const selectUntitledPhotos = useCallback(() => {
    const shouldScroll = baseSelectUntitledPhotos();
    if (shouldScroll) {
      setTimeout(() => {
        scrollToProcessingOptions();
      }, 100);
    }
  }, [baseSelectUntitledPhotos, scrollToProcessingOptions]);

  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loadingPreferences || loadingFreeTrial) {
        console.warn('Loading states timed out, forcing enable button');
        setLoadingPreferences(false);
        ensureFreeTrialStatus();
      }
    }, 15000);

    return () => clearTimeout(safetyTimeout);
  }, [loadingPreferences, loadingFreeTrial, ensureFreeTrialStatus]);

  const handleStartProcessing = useCallback(async () => {
    if (selectedPhotos.size === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    setSettingUpBatch(true);
    try {
      const photoIds = Array.from(selectedPhotos);
      const response = await axios.post(
        '/api/photos/process',
        {
          photoIds,
          provider,
          style: selectedStyle,
          descriptionSentences,
          addLenscribeTag: true
        },
        { withCredentials: true }
      );

      if (response.data.requiresPayment) {
        toast.success('Job created successfully');
        navigate(`/payment/${response.data.jobId}`);
      } else {
        toast.success('Job started successfully');
        navigate(`/job/${response.data.jobId}`);
      }
    } catch (error) {
      toast.error('Failed to start processing');
    } finally {
      setSettingUpBatch(false);
    }
  }, [
    descriptionSentences,
    navigate,
    provider,
    selectedPhotos,
    selectedStyle
  ]);

  if (!user || checkingProvider) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-64">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-600" />
          <p>Redirecting to connect a photo platform...</p>
        </div>
      </div>
    );
  }

  return (
    <PhotoSelectionContent
      photos={photos}
      loading={loading}
      criteriaCollapsed={criteriaCollapsed}
      setCriteriaCollapsed={setCriteriaCollapsed}
      albums={albums}
      fetchingAlbums={fetchingAlbums}
      selectedAlbum={selectedAlbum}
      setSelectedAlbum={setSelectedAlbum}
      getFeaturedAlbums={getFeaturedAlbums}
      handleAlbumSelect={handleAlbumSelect}
      handleQuickAction={handleQuickAction}
      tags={tags}
      setTags={setTags}
      handleTagSearch={handleTagSearch}
      uploadStart={uploadStart}
      uploadEnd={uploadEnd}
      takenStart={takenStart}
      takenEnd={takenEnd}
      handleDateChange={handleDateChange}
      fetchPhotos={fetchPhotos}
      selectedPhotos={selectedPhotos}
      togglePhoto={togglePhoto}
      toggleSelectAll={toggleSelectAll}
      selectUntitledPhotos={selectUntitledPhotos}
      scrollToProcessingOptions={scrollToProcessingOptions}
      freeTrialStatus={freeTrialStatus}
      processingOptionsRef={processingOptionsRef}
      selectedStyle={selectedStyle}
      setSelectedStyle={setSelectedStyle}
      descriptionSentences={descriptionSentences}
      setDescriptionSentences={setDescriptionSentences}
      settingUpBatch={settingUpBatch}
      loadingPreferences={loadingPreferences}
      loadingFreeTrial={loadingFreeTrial}
      handleStartProcessing={handleStartProcessing}
      getExampleMetadata={buildExampleMetadata}
    />
  );
};

export default PhotoSelectionContainer;
